import React, { useState, useEffect, useRef } from "react";
import { ReactComponent as InfoIcon } from "assets/icons/info.svg";
import useProperties from "hooks/useProperties";
import { formatCurrency } from "utils/formatNumber";
import { motion } from "framer-motion";
import { ReactComponent as CallIcon } from "assets/icons/call_icon.svg";
import { useSearchParams } from "react-router-dom";
import useSearchFilter from "hooks/useSearchFilter";
import HousePriceCalculatorDialog from "components/Dialog/HousePriceCalculatorDialog";

const PropertyInfo = () => {
  const { propertyDetails: property } = useProperties();
  const [isContactAgentDropdownOpen, setIsContactAgentDropdownOpen] =
    useState(false);
  const dropdownRef = useRef(null);
  const [searchParams] = useSearchParams();
  const { toggleHousePriceCalculatorDialog } = useSearchFilter();
  const averageGrossYield = Number(searchParams.get("average_gross_yield"));
  const grossYield = Number(property?.gross_yield);

  const isYieldAboveAverage = grossYield > averageGrossYield;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsContactAgentDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter listings with agency info
  const agencyListings = property?.listings?.filter(
    (listing) => listing?.agency && listing?.contacts_info?.phone
  );

  return (
    <div className="flex justify-between items-start lg:space-x-[32px] px-5 sm:px-6  flex-col lg:flex-row">
      <div className="flex-1">
        <div className="flex  md:items-center justify-between  md:space-x-3 lg:space-x-5 flex-col md:flex-row">
          <div>
            <h2 className="leading-[150%] sm:leading-[48px] text-[24px] sm:text-[32px] font-bold tracking-[-1%] text-black">
              {formatCurrency(property?.sale_price, property?.sale_currency)}
            </h2>
            <h1 className="text-[16px] sm:text-[18px] font-light text-black leading-[150%] sm:leading-[27px] tracking-[-1%] line-clamp-1">
              {property?.title}
            </h1>
          </div>
          <div className="text-black mt-3 md:mt-0 grid grid-cols-3 items-center h-[63px] w-full flex-1 md:max-w-[475px] md:min-w-[400px]">
            <div className="border-x border-[#F0F0F0]">
              <h3 className="font-semibold leading-[42px] text-[28px] text-center tracking-[-1%]">
                {property?.bedrooms || 0}
              </h3>
              <p className="text-[14px] font-light tracking-[-1%] text-center leading-[21px]">
                Camere da Letto
              </p>
            </div>
            <div>
              <h3 className="font-semibold leading-[42px] text-[28px] text-center tracking-[-1%]">
                {property?.bathrooms || 0}
              </h3>
              <p className="text-[14px] font-light tracking-[-1%] text-center leading-[21px]">
                Bagni
              </p>
            </div>
            <div className="border-l border-[#F0F0F0]">
              <h3 className="font-semibold leading-[42px] text-[28px] text-center tracking-[-1%]">
                {property?.total_area || 0}
              </h3>
              <p className="text-[14px] font-light tracking-[-1%] text-center leading-[21px]">
                Metri Quadri
              </p>
            </div>
          </div>
        </div>
        <div className="h-[72px] mt-5"></div>
        {/* <div className="h-[72px] mt-5 border-t border-[#F0F0F0] flex items-center py-3 space-x-3">
          <div className="w-[34px] h-[42.5px] flex items-center justify-center">
            <InfoIcon className="text-[#DC3545] w-[28.33px] h-[28.33px]" />
          </div>
          <div>
            <h3 className="text-[16px] font-semibold leading-[24px] text-black tracking-[-1%]">
              Avviso Rendita Locativa (
              {isYieldAboveAverage ? "higher" : "lower"})
            </h3>
            <p className="text-[14px] md:text-[16px] font-light leading-[24px] text-black tracking-[-1%] line-clamp-1">
              Questa proprietà ha un ritorno sull'investimento inferiore stimato
              rispetto ad altre nella stessa area
            </p>
          </div>
        </div> */}
      </div>
      <div className="fixed bottom-0 left-0 right-0 bg-white z-[11] md:static main-search-shadow md:shadow-none md:z-10 flex flex-col gap-3 p-5 border border-[#F0F0F0] md:rounded-[12px] md:w-[342px]">
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsContactAgentDropdownOpen((prev) => !prev)}
            className="bg-primary w-full rounded-[8px] flex items-center justify-center h-[49px] leading-[30px] text-[15px] font-semibold text-white"
          >
            Contatta Agenzia
          </button>
          {isContactAgentDropdownOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="absolute bg-white bottom-[55px] md:bottom-auto md:top-[55px] right-0 shadow-lg rounded-[8px] p-3 z-[9999] w-full"
            >
              <div className="scroll-container overflow-x-hidden max-h-[200px] rounded-[9px] border-[1px] border-[#F0EFFB]">
                <div className="pl-[14px] flex-1 lg:pr-[14px]">
                  {agencyListings?.length > 0 ? (
                    <table className="w-full text-left">
                      <tbody>
                        {agencyListings.map((listing, index) => (
                          <tr
                            key={index}
                            className={`${
                              agencyListings.length - 1 === index
                                ? "border-none"
                                : "border-b-[1px]"
                            } w-full border-[#F0F0F0] h-[37px]`}
                          >
                            <td className="border-r-[1px] pr-2">
                              <span className="text-sm font-semibold line-clamp-1 overflow-hidden">
                                {listing.agency}
                              </span>
                            </td>
                            <td className="text-[10px] font-semibold text-[#333D43] leading-[1.2] text-center pl-2">
                              <p className="h-full flex items-center justify-between space-x-2">
                                <span className="line-clamp-1">
                                  {listing.contacts_info?.phone}
                                </span>
                                <a href={`tel:${listing.contacts_info?.phone}`}>
                                  <CallIcon className="w-3 h-3 cursor-pointer text-primary" />
                                </a>
                              </p>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-center py-4 text-gray-500">
                      Nessun agente disponibile al momento. Ti invitiamo a
                      riprovare più tardi o contattare HouseScan direttamente.
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </div>
        <button
          onClick={() =>
            toggleHousePriceCalculatorDialog("propertyDetailsCalculator")
          }
          className="bg-[#F0F0F0] w-full rounded-[8px] flex items-center justify-center h-[44px] leading-[30px] text-[15px] font-semibold text-black border border-[#F0F0F0]"
        >
          Calcola Costi Nascosti
        </button>
      </div>
      <HousePriceCalculatorDialog id="propertyDetailsCalculator" />
    </div>
  );
};

export default PropertyInfo;
